import { AUDIT_TARGET_TYPES, type AuditTargetType } from '@planuze/admin-contracts';
import type { AdminLocale } from '../../content/locale.js';

/* Vocabulário da trilha de auditoria (ADR-0708). As `action` conhecidas derivam do mapeamento
   declarativo do admin-service (`lib/action-mapping.ts`, ADR-0346): mantê-las em sincronia é
   responsabilidade de quem adiciona uma rota mutante nova (o gate de cobertura
   `audit-route-coverage` já força o mapping no backend). Aqui só o rótulo amigável, consumido
   pela faceta de ação e pelo badge da tabela. `unmapped` é o sentinel do middleware p/ rota
   mutante sem mapping.

   `locale` é OPCIONAL (default pt-BR) e não um `Bilingual<T>`/`useBilingual`: `auditTargetTypeLabel`
   também é consumido por `routes/audit-page.tsx`, que ainda chama com um único argumento (mesma
   razão de `pack-catalog-labels.ts`/`releases-error.ts`). */
const ACTION_LABELS: Readonly<Record<AdminLocale, Readonly<Record<string, string>>>> = {
  'pt-BR': {
    'company.create': 'Empresa criada',
    'company.assign_plan': 'Plano da empresa atribuído',
    'company.block': 'Empresa bloqueada',
    'company.unblock': 'Empresa desbloqueada',
    'company.bulk': 'Governança em lote de empresas',
    'user.impersonate.start': 'Impersonation iniciada',
    'user.impersonate.end': 'Impersonation encerrada',
    'license.revoke': 'Licença revogada',
    'license.transfer': 'Licença transferida',
    'license.issue_manual': 'Licença emitida manualmente',
    'device.cooldown': 'Dispositivo em cooldown',
    'session.revoke': 'Sessão revogada',
    'plan.create': 'Plano criado',
    'plan.update': 'Plano atualizado',
    'plan.archive': 'Plano arquivado',
    'ticket.assign': 'Ticket atribuído',
    'ticket.close': 'Ticket encerrado',
    'pack.start_review': 'Revisão de pack iniciada',
    'pack.reopen': 'Revisão de pack reaberta',
    'pack.approve': 'Pack aprovado',
    'pack.reject': 'Pack rejeitado',
    'pack.request_changes': 'Mudanças solicitadas ao pack',
    'pack.takedown': 'Pack removido (takedown)',
    'pack.moderation_bulk': 'Moderação de packs em lote',
    'pack.bulk_takedown': 'Takedown de packs em lote',
    'publisher.provision': 'Publisher provisionado',
    'publisher.verify': 'Publisher verificado',
    'publisher.bulk': 'Ação em lote de publishers',
    'publisher.status': 'Status de publisher alterado',
    'config.update': 'Configuração atualizada',
    'faq.create': 'FAQ criada',
    'faq.update': 'FAQ atualizada',
    'faq.delete': 'FAQ removida',
    'faq.bulk_delete': 'Tópicos do FAQ removidos em lote',
    'notification.create': 'Notificação criada',
    'notification.publish': 'Notificação publicada',
    'notification.delete': 'Notificação removida',
    'release.register': 'Release registrada',
    'release.register_discovered': 'Releases registradas por descoberta',
    'release.promote': 'Release promovida',
    'release.update_notes': 'Notas de release atualizadas',
    'release.takedown': 'Release removida (takedown)',
    'release.purge_r2': 'Versão de release excluída do R2',
    /* Documentos legais (ADR-0774). `version_publish` é a ação de maior consequência do módulo:
       publicar torna a versão imutável e a coloca em vigor para o app e o site. */
    'legal.version_create': 'Versão de documento legal criada',
    'legal.version_update': 'Versão de documento legal atualizada',
    'legal.version_publish': 'Versão de documento legal publicada',
    'legal.version_delete': 'Versão de documento legal removida',
    'legal.bulk_delete_drafts': 'Rascunhos legais removidos em lote',
    'staff.bulk_update_status': 'Status de operadores alterado em lote',
    unmapped: 'Ação não mapeada',
  },
  'en-US': {
    'company.create': 'Company created',
    'company.assign_plan': 'Company plan assigned',
    'company.block': 'Company blocked',
    'company.unblock': 'Company unblocked',
    'company.bulk': 'Bulk company governance',
    'user.impersonate.start': 'Impersonation started',
    'user.impersonate.end': 'Impersonation ended',
    'license.revoke': 'License revoked',
    'license.transfer': 'License transferred',
    'license.issue_manual': 'License issued manually',
    'device.cooldown': 'Device in cooldown',
    'session.revoke': 'Session revoked',
    'plan.create': 'Plan created',
    'plan.update': 'Plan updated',
    'plan.archive': 'Plan archived',
    'ticket.assign': 'Ticket assigned',
    'ticket.close': 'Ticket closed',
    'pack.start_review': 'Pack review started',
    'pack.reopen': 'Pack review reopened',
    'pack.approve': 'Pack approved',
    'pack.reject': 'Pack rejected',
    'pack.request_changes': 'Changes requested for pack',
    'pack.takedown': 'Pack removed (takedown)',
    'pack.moderation_bulk': 'Bulk pack moderation',
    'pack.bulk_takedown': 'Bulk pack takedown',
    'publisher.provision': 'Publisher provisioned',
    'publisher.verify': 'Publisher verified',
    'publisher.bulk': 'Bulk publisher action',
    'publisher.status': 'Publisher status changed',
    'config.update': 'Config updated',
    'faq.create': 'FAQ created',
    'faq.update': 'FAQ updated',
    'faq.delete': 'FAQ removed',
    'faq.bulk_delete': 'FAQ topics removed in bulk',
    'notification.create': 'Notification created',
    'notification.publish': 'Notification published',
    'notification.delete': 'Notification removed',
    'release.register': 'Release registered',
    'release.register_discovered': 'Releases registered via discovery',
    'release.promote': 'Release promoted',
    'release.update_notes': 'Release notes updated',
    'release.takedown': 'Release removed (takedown)',
    'release.purge_r2': 'Release version deleted from R2',
    'legal.version_create': 'Legal document version created',
    'legal.version_update': 'Legal document version updated',
    'legal.version_publish': 'Legal document version published',
    'legal.version_delete': 'Legal document version removed',
    'legal.bulk_delete_drafts': 'Legal drafts removed in bulk',
    'staff.bulk_update_status': 'Operator status changed in bulk',
    unmapped: 'Unmapped action',
  },
};

/** Rótulo de uma `action`; ação desconhecida cai no próprio código (fail-soft). */
export function auditActionLabel(action: string, locale: AdminLocale = 'pt-BR'): string {
  return ACTION_LABELS[locale][action] ?? action;
}

/** Opções da faceta de ação (ordenadas pelo rótulo no locale corrente). */
export function auditActionOptions(
  locale: AdminLocale = 'pt-BR',
): readonly { readonly value: string; readonly label: string }[] {
  return Object.entries(ACTION_LABELS[locale])
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

/* `switch` (não Record) porque o enum inclui chaves snake_case (`negotiated_discount`/
   `staff_user`) e `useNamingConvention` rejeita propriedade snake_case; case com string-literal
   não é flagueado (mesmo padrão do `auditTargetTypeLabel` original). */
function targetTypeLabelPtBr(type: AuditTargetType | 'unknown'): string {
  switch (type) {
    case 'company':
      return 'Empresa';
    case 'user':
      return 'Usuário';
    case 'license':
      return 'Licença';
    case 'device':
      return 'Dispositivo';
    case 'plan':
      return 'Plano';
    case 'negotiated_discount':
      return 'Plano negociado';
    case 'ticket':
      return 'Ticket';
    case 'staff_user':
      return 'Operador';
    case 'session':
      return 'Sessão';
    case 'pack':
      return 'Pack';
    case 'publisher':
      return 'Publisher';
    case 'config':
      return 'Configuração';
    case 'faq':
      return 'FAQ';
    case 'release':
      return 'Release';
    case 'notification':
      return 'Notificação';
    case 'legal':
      return 'Documento legal';
    case 'unknown':
      return 'Desconhecido';
  }
}

function targetTypeLabelEnUs(type: AuditTargetType | 'unknown'): string {
  switch (type) {
    case 'company':
      return 'Company';
    case 'user':
      return 'User';
    case 'license':
      return 'License';
    case 'device':
      return 'Device';
    case 'plan':
      return 'Plan';
    case 'negotiated_discount':
      return 'Negotiated plan';
    case 'ticket':
      return 'Ticket';
    case 'staff_user':
      return 'Operator';
    case 'session':
      return 'Session';
    case 'pack':
      return 'Pack';
    case 'publisher':
      return 'Publisher';
    case 'config':
      return 'Config';
    case 'faq':
      return 'FAQ';
    case 'release':
      return 'Release';
    case 'notification':
      return 'Notification';
    case 'legal':
      return 'Legal document';
    case 'unknown':
      return 'Unknown';
  }
}

/* Rótulo do tipo de alvo — fonte única p/ a faceta de tipo e a coluna Alvo da tabela. */
export function auditTargetTypeLabel(type: AuditTargetType, locale: AdminLocale = 'pt-BR'): string {
  return locale === 'en-US' ? targetTypeLabelEnUs(type) : targetTypeLabelPtBr(type);
}

/** Opções da faceta de tipo de alvo (ordem canônica de `AUDIT_TARGET_TYPES`). */
export function auditTargetTypeOptions(
  locale: AdminLocale = 'pt-BR',
): readonly { readonly value: string; readonly label: string }[] {
  return AUDIT_TARGET_TYPES.map((value) => ({ value, label: auditTargetTypeLabel(value, locale) }));
}
